import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toDataUrl } from '../../../utils/common';
import type { ReportHeaderData } from './model';

export interface ReportHeaderProps {
    reportHeaderData: ReportHeaderData;
    name: string;
}

const logo = new URL('@/public/branding/neurovibes-logo.svg', import.meta.url).href;
const waveBg = new URL('@/public/report/header-wave.png', import.meta.url).href;

const ReportHeader = ({ reportHeaderData, name }: ReportHeaderProps) => {
    const { t } = useTranslation(['overallTraineeReport', 'traineeReports']);

    const [logoBase64, setLogoBase64] = useState<string | null>(null);
    const [waveBase64, setWaveBase64] = useState<string | null>(null);

    useEffect(() => {
        toDataUrl(logo)
            .then(setLogoBase64)
            .catch(err => {
                console.warn(`ReportHeader: failed to generate base64 for logo`, err);
            });
        toDataUrl(waveBg)
            .then(setWaveBase64)
            .catch(err => {
                console.warn(`ReportHeader: failed to generate base64 for wave background`, err);
            });
    }, []);

    return (
        <div>
            <header className="relative overflow-hidden rounded-md bg-white p-6 print:shadow-none">
                <img
                    src={waveBase64 || waveBg} // base64 fallback to original URL
                    alt=""
                    aria-hidden="true"
                    className="pointer-events-none absolute top-0 right-0 h-full w-auto max-w-none object-cover opacity-90 select-none"
                />
                <div className="pointer-events-none absolute inset-0 bg-gradient-to-r from-white via-white/80 to-white/20" />
                <div className="relative flex items-start justify-between">
                    <div className="max-w-xl">
                        <h1 className="text-[32px] font-semibold text-neutral-900">
                            {t('header.reportTitle', { name })}
                        </h1>
                        <p className="mt-4 text-lg font-semibold text-neutral-800">{reportHeaderData.programName}</p>
                        <p className="mt-1 text-base text-neutral-500">{reportHeaderData.status}</p>
                    </div>
                    <img
                        src={logoBase64 || logo}
                        alt="NeuroVibes"
                        className="ml-4 h-7 w-auto drop-shadow-sm"
                        draggable={false}
                    />
                </div>
            </header>
        </div>
    );
};

export default ReportHeader;
